import ScreenLayout from '@/components/layout/ScreenLayout'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { cn } from '@/lib/utils'
import {
  CHECKOUT_COLUMNS,
  OPTIONS_COLUMN,
} from '@/screens/account/components/checkout/checkoutLayout'

/** A bar centred in one line of `text` — the loaded line's own box, so nothing grows. */
function Line({ text, className }: { text: string; className: string }) {
  return (
    <div className={cn('flex h-lh items-center', text)}>
      <Skeleton className={className} />
    </div>
  )
}

/** A radio row's box: `py-3`, an 18px title, 4, a 17px description. */
function OptionRowSkeleton() {
  return (
    <div className="flex items-start gap-3 border-b border-border-subtle py-3 last:border-b-0">
      <Skeleton className="size-6 shrink-0" />
      <div className="min-w-0 flex-1">
        <Line text="text-label-l" className="h-3 w-24" />
        <div className="mt-1">
          <Line text="text-body-xs" className="h-3 w-40" />
        </div>
      </div>
    </div>
  )
}

/**
 * `/courses/$courseId/checkout` before the course loads — only on a cold link,
 * since the course's own page leaves the same read cached. The loaded page's
 * blocks on its own grid (`checkoutLayout.ts`, shared with the subscription
 * checkout): the course card, "How you pay" at its three gateways, and the
 * summary. The title row is static, so it is the real one.
 *
 * Three guesses, all about data: a one-line title, a meta line that wraps to
 * a second line below the options column's `@md` (a typical "Beginner ·
 * Nepali · 37 lectures · 26 h 40 m"), and a sale (two summary rows, the list
 * price and its discount) — the page's other shape, a plain price, is one
 * row shorter.
 *
 * Measured against the loaded page for course 102 (the E2E fixture), block
 * by block, card / "How you pay" / summary: 1440 is 132 / 254 / 298 in both,
 * 1024 is 121 / 246 / 298 and 390 is 121 / 246 / 290.
 */
export default function CourseCheckoutPending() {
  return (
    <ScreenLayout
      header={<TitleRow title="Checkout" back="/courses/$courseId" />}
      fullWidth
      pending
    >
      <div className={CHECKOUT_COLUMNS}>
        <div className={OPTIONS_COLUMN}>
          <div className={cn(SECTION_CARD, 'flex items-start gap-4')}>
            <Skeleton className="aspect-video w-24 shrink-0 rounded-md @md/options:w-40" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Line text="text-h-m" className="h-4 w-3/5" />
              <div>
                <Line text="text-body-s" className="h-3 w-2/5" />
                <div className="@md/options:hidden">
                  <Line text="text-body-s" className="h-3 w-1/3" />
                </div>
              </div>
              <Line text="text-body-s" className="h-3 w-1/4" />
            </div>
          </div>
          <div className={cn(SECTION_CARD, 'min-w-0')}>
            <Line text="text-body-xs" className="h-3 w-20" />
            <div className="mt-1">
              {Array.from({ length: 3 }, (_, row) => (
                <OptionRowSkeleton key={row} />
              ))}
            </div>
          </div>
        </div>
        <div className={cn(SECTION_CARD, 'min-w-0')}>
          <Line text="text-body-xs" className="h-3 w-24" />
          <div className="mt-1">
            {Array.from({ length: 2 }, (_, row) => (
              <div
                key={row}
                className="flex items-center justify-between gap-4 border-b border-border-subtle py-3"
              >
                <Line text="text-body-m" className="h-3 w-32" />
                <Line text="text-label-l" className="h-3 w-16" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-4 pt-4">
            <Line text="text-h-s" className="h-3 w-20" />
            <Line text="text-h-xl" className="h-6 w-24" />
          </div>
          <Skeleton className="mt-5 h-12 w-full @2xl:w-32" />
          <div className="mt-3">
            <Line text="text-body-xs" className="h-3 w-48" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}
